'use client'

import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CatHoverContext } from './CatHoverLink'

type CatImageProps = {
  /** Cat number. Artwork is self hosted at /cats/<n>.png and /cats/<n>.gif. */
  n: number
  alt: string
  /** False when we have no artwork for this cat yet. */
  hasImage?: boolean
  className?: string
  /** Fired once the still is on screen, or immediately if it never will be. */
  onLoad?: () => void
}

/**
 * The still PNG for a cat, swapped for the animated GIF while the wrapping card
 * is hovered or focused. The GIF src is only set once the pointer arrives, so a
 * grid of hundreds of cats never downloads a single animation up front.
 */
export default function CatImage({
  n,
  alt,
  hasImage = true,
  className,
  onLoad,
}: CatImageProps) {
  const hovered = useContext(CatHoverContext)
  const [failed, setFailed] = useState(false)
  // A GIF that will not load must not be requested again on the next hover.
  const [gifFailed, setGifFailed] = useState(false)
  const ref = useRef<HTMLImageElement>(null)

  const animated = hovered && !gifFailed
  const unavailable = !hasImage || failed

  // An image that is already decoded when React hydrates (a warm cache, or the
  // server-rendered markup) never fires `load`, which would leave the card
  // stuck behind its skeleton forever.
  useEffect(() => {
    if (unavailable || ref.current?.complete) onLoad?.()
  }, [unavailable, onLoad])

  const handleError = useCallback(() => {
    // A missing GIF just means no animation: fall back to the still and stop
    // asking for it. Only a missing PNG means there is no artwork.
    if (animated) {
      setGifFailed(true)
      return
    }
    setFailed(true)
  }, [animated])

  if (unavailable) {
    return (
      <div
        className={`flex aspect-square w-full items-center justify-center bg-dark-7 text-gray-4 ${className || ''}`}
        role="img"
        aria-label={`${alt} (artwork unavailable)`}
      >
        <span className="text-2xl font-bold">#{n}</span>
      </div>
    )
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={animated ? `/cats/${n}.gif` : `/cats/${n}.png`}
      alt={alt}
      width={1000}
      height={1000}
      loading="lazy"
      decoding="async"
      className={className}
      style={{
        width: '100%',
        height: 'auto',
        display: 'block',
        imageRendering: 'pixelated',
      }}
      onLoad={onLoad}
      // A handful of cats have no artwork yet; never show a broken image.
      onError={handleError}
    />
  )
}
